/**
 * 廠商表單(REQ-20260522 Phase 1B-Console)。
 * 視覺與客戶 form 同款:12-col grid + emerald 配色 + 固定電話槽位
 * (PHONE×3 + MOBILE×2 + FAX×3),不再做動態 phones 加槽。
 *
 * REQ-20260902004 改成圖二的排列:稅別 / 網站開啟鈕 / 銀行代號 / 付款條件下拉,
 * 行動電話改成「聯絡人一 / 二行動」兩格(吃 MOBILE 的兩個槽位),
 * 電話與傳真的畫面格數縮成 2(**槽位仍是 3**,見 emptySlots 的註解)。
 * 稅別那格的標題原為「稅額」,REQ-20260902010 改成「稅別」(欄位仍是 taxMode)。
 * 廠商編號的自動聚焦原本寫死在 input 上(REQ-20260803002),REQ-20260905005 改由
 * 父層以 autoFocusCode 決定:只有新增模式聚焦,檢視 / 切換廠商時不再搶走游標。
 * REQ-20260916004 在 <form> 上掛了一支鍵盤跳格代理(見 focusSiblingOnKey):
 * Enter 與左右方向鍵在欄位之間移動,Enter 一律不再送出表單。
 *
 * forwardRef + useImperativeHandle pattern,console 用 ref.submit() 觸發送出。
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateSupplierSchema,
  type CreateSupplierInput,
  type SupplierPhoneInput,
  type SupplierView,
} from '@app/shared';
import { AddressInput } from '../../components/AddressInput';
import { AddressMapButton, WebsiteOpenButton } from '../../components/LinkOutButtons';
import { useReferences } from '../settings/api';

// ── 樣式 token(對齊 CustomerForm) ────────────────────────────
const inputCls =
  'w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 disabled:bg-slate-100 disabled:text-slate-500';
const labelCls = 'text-xs font-bold text-emerald-800';
/**
 * 欄位級衝突(後端 409)的紅框樣式。與 inputCls 同結構,只換邊框 / focus 顏色 ——
 * 刻意不用 `${inputCls} border-red-400` 串接,因為 Tailwind 產出的 CSS 順序
 * 不保證後寫的 border-color 會勝出。
 */
const inputConflictCls =
  'w-full border border-red-400 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 disabled:bg-slate-100 disabled:text-slate-500';

// Tailwind JIT 需要 static class names — 列出所有用到的 col-span 值
const SPAN_CLS: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

// ── 電話固定槽位:3 phone + 2 mobile + 3 fax
interface PhoneSlots {
  phone: [string, string, string];
  /** 聯絡人一 / 聯絡人二的行動(REQ-20260902004,吃既有 supplier_phones 的 MOBILE 型別) */
  mobile: [string, string];
  fax: [string, string, string];
}

/**
 * ⚠️ phone / fax 仍然是 **3 槽**,但 REQ-20260902004 之後畫面只 render 前 2 格
 * (見 PhoneSlotGroup 的 visibleCount)。第三槽刻意留著:後端 update 對 phones 是
 * 「刪光重建」,只要第三筆不再出現在送出的 payload,既有廠商的第三支電話 / 傳真
 * 就會被永久刪掉。留著看不見的槽位 = 載入時讀得回來、送出時原封不動送回去。
 */
function emptySlots(): PhoneSlots {
  return { phone: ['', '', ''], mobile: ['', ''], fax: ['', '', ''] };
}

/**
 * 付款條件的固定選項(REQ-20260902004)。欄位型別沒變(仍是自由文字 VarChar(30)),
 * 存的就是這些中文字 —— 下拉只是把常用值列出來,不是 enum。
 */
const PAYMENT_TERMS_OPTIONS: readonly string[] = ['回郵', '匯款', '現金', '預付'];

function nullIfEmpty(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length === 0 ? null : s;
}

function nullableNumber(v: unknown): number | null | undefined {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export interface SupplierFormHandle {
  submit: () => void;
  isDirty: () => boolean;
}

export interface SupplierFormProps {
  defaultValues?: SupplierView | undefined;
  readOnly?: boolean | undefined;
  onSubmit: (values: CreateSupplierInput) => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** 後端 409 指名的重複欄位(REQ-20260803001):該欄位加紅框 + 欄位下紅字 */
  conflictField?: 'code' | null | undefined;
  /** 搭配 conflictField 顯示在該欄位下方的訊息 */
  conflictMessage?: string | null | undefined;
  /** 掛載時是否把游標放在「廠商編號」欄(REQ-20260905005:只有新增模式要) */
  autoFocusCode?: boolean | undefined;
}

/**
 * 表單內依 DOM 順序排好的輸入格(REQ-20260916004)。
 *
 * ⚠️ 刻意**不用** `offsetParent` / `checkVisibility` / `getBoundingClientRect` 過濾
 * 「看不看得見」—— jsdom 沒有版面,那幾個判斷一律 falsy,清單會被濾成 0 個,
 * 跳格靜默失效而測試照樣全綠。`disabled` 與 `type="hidden"` 是屬性判斷,
 * 在 jsdom 與瀏覽器裡結果一致,所以只濾這兩個。
 */
function formFields(form: HTMLFormElement): HTMLElement[] {
  const all = form.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >('input, select, textarea');
  return Array.from(all).filter(
    (el) => !el.disabled && !(el instanceof HTMLInputElement && el.type === 'hidden'),
  );
}

/**
 * 游標是否已經停在字串的最前 / 最後,而且沒有選取範圍
 * (REQ-20260904002 / REQ-20260910001 的同一條規則:游標在字中間或有選取範圍時
 * 方向鍵一律維持原生行為,否則使用者打錯字就再也移不回去改)。
 *
 * ⚠️ `type="email"` / `type="url"` / `type="number"` 的 input 讀 `selectionStart`
 * 會拋 `InvalidStateError`(Chrome)或回 `null`。取不到值時一律回 `false`
 * = **不跳格**(保守):寧可少跳一次,也不要在使用者還在改字時把焦點搬走。
 * 那幾格的 Enter 不受影響,照樣跳。刻意不為此把 type 改成 text —— 會失去瀏覽器驗證。
 */
function atCaretEdge(el: HTMLInputElement, edge: 'start' | 'end'): boolean {
  try {
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start == null || end == null || start !== end) return false;
    return edge === 'start' ? start === 0 : start === el.value.length;
  } catch {
    return false;
  }
}

/**
 * 主檔表單的鍵盤跳格(REQ-20260916004)。掛在 `<form>` 上做事件代理,依 DOM 順序
 * 找下一 / 上一格 —— 這張是 20 多格的 12-col grid,照品項列那種「逐格配對 ref」
 * 的寫法(REQ-20260904002 / REQ-20260911001)會多出二十幾個 ref 與接線。
 *
 * 既有規則照抄,不重新論證:組字中的 Enter 是**選字**不是跳格;方向鍵只在游標
 * 已經到邊界時才跳;焦點延一幀才移(讓瀏覽器先把它那一份做完);**不加任何 tabIndex**。
 *
 * **Enter 一律 `preventDefault()`**:這張 `<form>` 內沒有任何 `type="submit"` 的按鈕,
 * 工具列的「儲存」走 `formRef.current.submit()`,所以 Enter 是目前唯一會觸發
 * implicit submission 的路徑 —— 需求就是要它不再存檔。最後一格按 Enter 停在原地。
 *
 * `<select>` 只攔 Enter:原生的 ← / → / ↑ / ↓ 是切換選項。
 * ↑ / ↓ 兩張表單都一鍵不攔 —— 格狀版面下「下一格」與「視覺上正下方那一格」
 * 不是同一件事,猜錯比留白貴。
 */
function focusSiblingOnKey(e: React.KeyboardEvent<HTMLFormElement>): void {
  if (e.nativeEvent.isComposing) return;
  const target = e.target;
  if (
    !(target instanceof HTMLInputElement) &&
    !(target instanceof HTMLSelectElement) &&
    !(target instanceof HTMLTextAreaElement)
  ) {
    return;
  }

  let step: 1 | -1;
  if (e.key === 'Enter') {
    // 備註是 textarea,那裡的 Enter 是換行
    if (target instanceof HTMLTextAreaElement) return;
    step = 1;
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    if (!(target instanceof HTMLInputElement)) return;
    if (!atCaretEdge(target, e.key === 'ArrowRight' ? 'end' : 'start')) return;
    step = e.key === 'ArrowRight' ? 1 : -1;
  } else {
    return;
  }

  e.preventDefault();
  const fields = formFields(e.currentTarget);
  const idx = fields.indexOf(target);
  const next = idx < 0 ? undefined : fields[idx + step];
  if (next) requestAnimationFrame(() => next.focus());
}

export const SupplierForm = forwardRef<SupplierFormHandle, SupplierFormProps>(function SupplierForm(
  props,
  ref,
) {
  const {
    defaultValues,
    readOnly,
    onSubmit,
    onDirtyChange,
    conflictField,
    conflictMessage,
    autoFocusCode,
  } = props;
  const disabled = readOnly === true;

  const { data: categories } = useReferences('supplier-categories', false);

  const [slots, setSlots] = useState<PhoneSlots>(emptySlots());
  const initialSlotsRef = useRef<string>(JSON.stringify(emptySlots()));
  const [slotsDirty, setSlotsDirty] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty: rhfDirty },
  } = useForm<CreateSupplierInput>({
    resolver: zodResolver(CreateSupplierSchema),
    defaultValues: {
      code: '',
      fullName: '',
      simpleName: '',
      tax: '',
      taxMode: undefined,
      categoryId: undefined,
      boss: '',
      contact1: '',
      contact2: '',
      email: '',
      website: '',
      address: '',
      addressShipping: '',
      paymentTerms: '',
      paymentDays: undefined,
      closingDay: undefined,
      bankCode: '',
      bankNo: '',
      bankAccount: '',
      openingPayable: undefined,
      openingPrepaid: undefined,
      notes: '',
      phones: [],
    },
  });

  // 進入 / 切換 supplier 時把欄位 + slots 重建
  useEffect(() => {
    if (defaultValues) {
      reset({
        code: defaultValues.code ?? '',
        fullName: defaultValues.fullName,
        simpleName: defaultValues.simpleName,
        tax: defaultValues.tax ?? '',
        taxMode: defaultValues.taxMode ?? undefined,
        categoryId: defaultValues.categoryId ?? undefined,
        boss: defaultValues.boss ?? '',
        contact1: defaultValues.contact1 ?? '',
        contact2: defaultValues.contact2 ?? '',
        email: defaultValues.email ?? '',
        website: defaultValues.website ?? '',
        address: defaultValues.address ?? '',
        addressShipping: defaultValues.addressShipping ?? '',
        paymentTerms: defaultValues.paymentTerms ?? '',
        paymentDays: defaultValues.paymentDays ?? undefined,
        closingDay: defaultValues.closingDay ?? undefined,
        bankCode: defaultValues.bankCode ?? '',
        bankNo: defaultValues.bankNo ?? '',
        bankAccount: defaultValues.bankAccount ?? '',
        openingPayable: defaultValues.openingPayable ?? undefined,
        openingPrepaid: defaultValues.openingPrepaid ?? undefined,
        notes: defaultValues.notes ?? '',
        phones: [],
      });
      // 拆 phones 進固定槽位:per-type 順序填(忽略 stored displayOrder,
      // 避免舊 mdb 匯入時 displayOrder = global array index 不適用 per-type 0/1/2)
      const next = emptySlots();
      let phoneIdx = 0;
      let faxIdx = 0;
      for (const p of defaultValues.phones) {
        if (p.phoneType === 'PHONE' && phoneIdx < 3) {
          next.phone[phoneIdx] = p.number;
          phoneIdx += 1;
        } else if (p.phoneType === 'MOBILE') {
          // 兩個行動槽位:displayOrder 0/1 直接對位;不是 0/1(舊 mdb 匯入的
          // displayOrder 是 global array index)或該槽已被佔走時,遞補到另一個空槽
          const preferred = p.displayOrder === 1 ? 1 : 0;
          const fallback = preferred === 0 ? 1 : 0;
          if (next.mobile[preferred] === '') next.mobile[preferred] = p.number;
          else if (next.mobile[fallback] === '') next.mobile[fallback] = p.number;
        } else if (p.phoneType === 'FAX' && faxIdx < 3) {
          next.fax[faxIdx] = p.number;
          faxIdx += 1;
        }
      }
      setSlots(next);
      initialSlotsRef.current = JSON.stringify(next);
      setSlotsDirty(false);
    } else {
      reset();
      const empty = emptySlots();
      setSlots(empty);
      initialSlotsRef.current = JSON.stringify(empty);
      setSlotsDirty(false);
    }
  }, [defaultValues, reset]);

  // REQ-20261001002:新開視窗 / 重新整理時廠商資料比類別選項先到,reset() 寫進 DOM 時
  // 還沒有那個 option → 回落到「— 請選擇 —」。選項一到就把目前的值重寫回 DOM;不帶 shouldDirty。
  useEffect(() => {
    setValue('categoryId', getValues('categoryId'));
  }, [categories, setValue, getValues]);

  // dirty 通知外層
  useEffect(() => {
    onDirtyChange?.(rhfDirty || slotsDirty);
  }, [rhfDirty, slotsDirty, onDirtyChange]);

  const updateSlot = (
    kind: 'phone' | 'fax',
    idx: number,
    value: string,
  ): void => {
    setSlots((prev) => {
      const next = { ...prev, phone: [...prev.phone] as PhoneSlots['phone'], fax: [...prev.fax] as PhoneSlots['fax'] };
      if (kind === 'phone') next.phone[idx] = value;
      else next.fax[idx] = value;
      return next;
    });
    setSlotsDirty(true);
  };

  const updateMobile = (idx: number, value: string): void => {
    setSlots((prev) => {
      const next = { ...prev, mobile: [...prev.mobile] as PhoneSlots['mobile'] };
      next.mobile[idx] = value;
      return next;
    });
    setSlotsDirty(true);
  };

  /**
   * 付款條件下拉的選項(REQ-20260902004)。既有廠商可能存著不在四個固定選項裡的值
   * (正式資料有「月結」),不把它補成一個 option 的話,使用者一開既有廠商再存檔
   * 就會被靜默清成空值。
   */
  const legacyPaymentTerm = defaultValues?.paymentTerms?.trim() ?? '';
  const paymentTermsOptions =
    legacyPaymentTerm && !PAYMENT_TERMS_OPTIONS.includes(legacyPaymentTerm)
      ? [legacyPaymentTerm, ...PAYMENT_TERMS_OPTIONS]
      : PAYMENT_TERMS_OPTIONS;

  const submitForm = handleSubmit((raw) => {
    const phones: SupplierPhoneInput[] = [];
    slots.phone.forEach((n, i) => {
      const trimmed = n.trim();
      if (trimmed) phones.push({ phoneType: 'PHONE', displayOrder: i, number: trimmed });
    });
    slots.mobile.forEach((n, i) => {
      const trimmed = n.trim();
      if (trimmed) phones.push({ phoneType: 'MOBILE', displayOrder: i, number: trimmed });
    });
    slots.fax.forEach((n, i) => {
      const trimmed = n.trim();
      if (trimmed) phones.push({ phoneType: 'FAX', displayOrder: i, number: trimmed });
    });
    const payload: CreateSupplierInput = {
      ...raw,
      code: nullIfEmpty(raw.code),
      tax: nullIfEmpty(raw.tax),
      boss: nullIfEmpty(raw.boss),
      contact1: nullIfEmpty(raw.contact1),
      contact2: nullIfEmpty(raw.contact2),
      website: nullIfEmpty(raw.website),
      address: nullIfEmpty(raw.address),
      addressShipping: nullIfEmpty(raw.addressShipping),
      paymentTerms: nullIfEmpty(raw.paymentTerms),
      bankCode: nullIfEmpty(raw.bankCode),
      bankNo: nullIfEmpty(raw.bankNo),
      bankAccount: nullIfEmpty(raw.bankAccount),
      notes: nullIfEmpty(raw.notes),
      phones,
    };
    onSubmit(payload);
  });

  useImperativeHandle(ref, () => ({
    submit: () => void submitForm(),
    isDirty: () => rhfDirty || slotsDirty,
  }));

  return (
    <form
      onSubmit={(e) => void submitForm(e)}
      onKeyDown={focusSiblingOnKey}
      className="grid gap-x-2 gap-y-2 md:grid-cols-12 text-sm bg-white border border-emerald-200 rounded p-3"
      data-testid="supplier-form"
    >
      {/* Row 1 — 編號 / 簡稱 / 統編 / 類別 / 負責人 / 稅別(各 2 欄) */}
      <Field
        label="廠商編號"
        error={
          errors.code?.message ??
          (conflictField === 'code' ? conflictMessage ?? undefined : undefined)
        }
        colSpan={2}
      >
        <input
          type="text"
          {...register('code')}
          autoFocus={autoFocusCode === true}
          className={conflictField === 'code' ? inputConflictCls : inputCls}
          disabled={disabled}
        />
      </Field>
      <Field label="簡稱 *" error={errors.simpleName?.message} colSpan={2}>
        <input type="text" {...register('simpleName')} className={inputCls} disabled={disabled} />
      </Field>
      <Field label="統一編號" error={errors.tax?.message} colSpan={2}>
        <input
          type="text"
          {...register('tax')}
          maxLength={8}
          className={inputCls}
          disabled={disabled}
        />
      </Field>
      <Field label="類別" error={errors.categoryId?.message} colSpan={2}>
        <select
          {...register('categoryId', { setValueAs: nullableNumber })}
          className={inputCls}
          disabled={disabled}
        >
          <option value="">— 請選擇 —</option>
          {(categories?.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="負責人" error={errors.boss?.message} colSpan={2}>
        <input type="text" {...register('boss')} className={inputCls} disabled={disabled} />
      </Field>
      <Field label="稅別" error={errors.taxMode?.message} colSpan={2}>
        <select
          {...register('taxMode', { setValueAs: nullIfEmpty })}
          className={inputCls}
          disabled={disabled}
        >
          <option value="">— 請選擇 —</option>
          <option value="INCLUSIVE">含稅</option>
          <option value="EXCLUSIVE">稅外加</option>
          <option value="EXEMPT">免稅</option>
        </select>
      </Field>

      {/* Row 2 — 公司全名 / 網站(各 6 欄) */}
      <Field label="公司全名 *" error={errors.fullName?.message} colSpan={6}>
        <input type="text" {...register('fullName')} className={inputCls} disabled={disabled} />
      </Field>
      {/* 輸入格 + 開啟鈕是兩個控制項共用一個標籤 → 用 GroupField(理由見該元件註解) */}
      <GroupField label="網站" error={errors.website?.message} colSpan={6}>
        <div className="flex gap-1">
          <input
            type="url"
            {...register('website')}
            aria-label="網站"
            placeholder="https://"
            /* min-w-0 不可省:input 的預設最小寬(約 172px)會把按鈕擠出格子 */
            className={`${inputCls} min-w-0`}
            disabled={disabled}
          />
          <WebsiteOpenButton value={watch('website')} />
        </div>
      </GroupField>

      {/* Row 3 — 聯絡人一 / 行動 / 聯絡人二 / 行動 / Email(2/2/2/2/4) */}
      <Field label="聯絡人一" error={errors.contact1?.message} colSpan={2}>
        <input type="text" {...register('contact1')} className={inputCls} disabled={disabled} />
      </Field>
      <Field label="聯絡人一行動" colSpan={2}>
        <input
          type="text"
          value={slots.mobile[0]}
          onChange={(e) => updateMobile(0, e.target.value)}
          className={inputCls}
          disabled={disabled}
        />
      </Field>
      <Field label="聯絡人二" error={errors.contact2?.message} colSpan={2}>
        <input type="text" {...register('contact2')} className={inputCls} disabled={disabled} />
      </Field>
      <Field label="聯絡人二行動" colSpan={2}>
        <input
          type="text"
          value={slots.mobile[1]}
          onChange={(e) => updateMobile(1, e.target.value)}
          className={inputCls}
          disabled={disabled}
        />
      </Field>
      <Field label="Email" error={errors.email?.message} colSpan={4}>
        <input type="email" {...register('email')} className={inputCls} disabled={disabled} />
      </Field>

      {/* Row 4 — 公司 / 工廠地址(各 6 欄) */}
      <GroupField label="公司地址" error={errors.address?.message} colSpan={6}>
        <AddressInput
          label="公司地址"
          value={watch('address')}
          onChange={(next) => setValue('address', next, { shouldDirty: true })}
          inputClassName={inputCls}
          disabled={disabled}
        >
          {/* 傳合併後的完整地址(含郵遞區號)—— 搜尋要帶區號,同 CustomerForm */}
          <AddressMapButton value={watch('address')} label="公司地址" />
        </AddressInput>
      </GroupField>
      <GroupField label="工廠地址" error={errors.addressShipping?.message} colSpan={6}>
        <AddressInput
          label="工廠地址"
          value={watch('addressShipping')}
          onChange={(next) => setValue('addressShipping', next, { shouldDirty: true })}
          placeholder="留空表示同公司地址"
          inputClassName={inputCls}
          disabled={disabled}
        >
          <AddressMapButton value={watch('addressShipping')} label="工廠地址" />
        </AddressInput>
      </GroupField>

      {/* Row 5 — 電話 / 傳真(各 2 格)/ 付款條件 / 收款天數 / 月結帳日(3/3/2/2/2) */}
      <PhoneSlotGroup
        label="電話"
        values={slots.phone}
        onChange={(i, v) => updateSlot('phone', i, v)}
        disabled={disabled}
        colSpan={3}
        visibleCount={2}
      />
      <PhoneSlotGroup
        label="傳真"
        values={slots.fax}
        onChange={(i, v) => updateSlot('fax', i, v)}
        disabled={disabled}
        colSpan={3}
        visibleCount={2}
      />
      <Field label="付款條件" error={errors.paymentTerms?.message} colSpan={2}>
        <select {...register('paymentTerms')} className={inputCls} disabled={disabled}>
          <option value="">— 請選擇 —</option>
          {paymentTermsOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Field>
      <Field label="收款天數" error={errors.paymentDays?.message} colSpan={2}>
        <input
          type="number"
          {...register('paymentDays', { setValueAs: nullableNumber })}
          className={inputCls}
          disabled={disabled}
        />
      </Field>
      <Field label="月結帳日(1-31)" error={errors.closingDay?.message} colSpan={2}>
        <input
          type="number"
          min={0}
          max={31}
          {...register('closingDay', { setValueAs: nullableNumber })}
          className={inputCls}
          disabled={disabled}
        />
      </Field>

      {/* Row 6 — 銀行 / 銀行代號 / 銀行帳號 / 期初應付 / 期初預付(3/2/3/2/2) */}
      <Field label="銀行" error={errors.bankCode?.message} colSpan={3}>
        <input type="text" {...register('bankCode')} className={inputCls} disabled={disabled} />
      </Field>
      <Field label="銀行代號" error={errors.bankNo?.message} colSpan={2}>
        <input type="text" {...register('bankNo')} className={inputCls} disabled={disabled} />
      </Field>
      <Field label="銀行帳號" error={errors.bankAccount?.message} colSpan={3}>
        <input type="text" {...register('bankAccount')} className={inputCls} disabled={disabled} />
      </Field>
      <Field label="期初應付" error={errors.openingPayable?.message} colSpan={2}>
        <input
          type="text"
          {...register('openingPayable')}
          placeholder="例:12500.00"
          className={inputCls}
          disabled={disabled}
        />
      </Field>
      <Field label="期初預付" error={errors.openingPrepaid?.message} colSpan={2}>
        <input
          type="text"
          {...register('openingPrepaid')}
          placeholder="例:0"
          className={inputCls}
          disabled={disabled}
        />
      </Field>

      {/* Row 7 — 備註全寬 */}
      <Field label="備註" error={errors.notes?.message} colSpan={12}>
        <textarea
          {...register('notes')}
          rows={3}
          className={inputCls}
          disabled={disabled}
        />
      </Field>
    </form>
  );
});

// ── 子元件 ────────────────────────────────────────────────────

function Field(props: {
  label: string;
  error?: string | undefined;
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  children: React.ReactNode;
}): JSX.Element {
  const span = SPAN_CLS[props.colSpan ?? 1] ?? '';
  return (
    <label className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{props.label}</span>
      {props.children}
      {props.error ? (
        <span role="alert" className="text-xs text-red-600">
          {props.error}
        </span>
      ) : null}
    </label>
  );
}

/**
 * 一個標籤配多個控制項的欄位列(REQ-20260901004 的地址列、REQ-20260902004 的網站列)。
 * 外觀與 `Field` 相同,但用 `div` + `span` 而不是包住 children 的 `<label>` ——
 * 郵遞區號 / 地址是兩個 input、網站是 input + 開啟鈕,包在同一個 label 裡會讓
 * `getByLabelText('公司地址')` 抓到多個元素(`<button>` 也是 labelable)。
 * 標籤關聯改由各控制項自己的 aria-label 承接,做法同 PhoneSlotGroup。
 */
function GroupField(props: {
  label: string;
  error?: string | undefined;
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  children: React.ReactNode;
}): JSX.Element {
  const span = SPAN_CLS[props.colSpan ?? 1] ?? '';
  return (
    <div className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{props.label}</span>
      {props.children}
      {props.error ? (
        <span role="alert" className="text-xs text-red-600">
          {props.error}
        </span>
      ) : null}
    </div>
  );
}

function PhoneSlotGroup(props: {
  label: string;
  values: readonly string[];
  onChange: (idx: number, value: string) => void;
  disabled: boolean;
  colSpan: number;
  /**
   * 實際 render 出來的格數。`values` 可以比它長 —— 多出來的槽位仍在 state 裡,
   * 送出時照樣進 payload,只是畫面上不顯示(REQ-20260902004,見 emptySlots 的註解)。
   */
  visibleCount: number;
}): JSX.Element {
  const span = SPAN_CLS[props.colSpan] ?? '';
  const gridCls =
    props.visibleCount === 3
      ? 'md:grid-cols-3'
      : props.visibleCount === 2
        ? 'md:grid-cols-2'
        : 'md:grid-cols-1';
  return (
    <div className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{props.label}</span>
      <div className={`grid gap-1 ${gridCls}`}>
        {props.values.slice(0, props.visibleCount).map((v, i) => (
          <input
            key={i}
            type="text"
            value={v}
            onChange={(e) => props.onChange(i, e.target.value)}
            placeholder={`${props.label} ${i + 1}`}
            className={inputCls}
            disabled={props.disabled}
            aria-label={`${props.label} ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
